import { Badge, Button, FieldLabel, Flex, FlexBox, Heading, Icon, InlineInput, Label, Tag, Textarea } from "@braze/beacon-core"
import { Drawer, DrawerBody, DrawerFooter } from "@braze/beacon-labs-drawer"
import React, { ChangeEvent, useEffect, useState } from "react";
import styled from "styled-components"
import { CampaignObject, MarketoProgramObject, ParentCampaignObject } from "../../types/APITypes";
import { getColor, useColor } from "@braze/beacon-styling";
import { StatusTag } from "./StatusTag";
import { ButtonLoadingState } from "@braze/beacon-hooks";
import { OnChangeAction } from "@braze/beacon-core/dist/components/InlineInput/types";
import { Select } from "@braze/beacon-lab-select";
import { BUDGET_SOURCE, CAMPAIGN_OBJECTIVE, CAMPAIGN_SOURCE } from '../../constants/SelectConstants';
import { OptionsList, formatOption } from "../../util/optionsFormatter";
import { useGlobalAppContext } from "../../contexts/GlobalAppContext";
import { useSaveCampaignChanges } from "../../hooks/useSaveCampaignChanges";
import { useApproveCampaign } from "../../hooks/useApproveCampaign";
import { useRejectCampaign } from "../../hooks/useRejectCampaign";
import { useResubmitCampaign } from "../../hooks/useResubmitCampaign";
import { EDITABLE_STATUS } from "../../constants/EditableStatus";

type CampaignDrawerProps = {

    campaign: CampaignObject;
};

export const CampaignDrawer = ({campaign}:CampaignDrawerProps) => {
    
    // const { setRefresh } = useOverviewContext();
    
    const colorGetter = useColor();
    const [open, setOpen] = useState(false);
    const [campaignState, setCampaignState] = useState(campaign);
    const [changed, setChanged] = useState(false);

    const [status, setStatus] = useState(campaign.approval_status);
    
    const { parentCampaigns, programs, setRefresh } = useGlobalAppContext();

    const [saveChanges, saveChangesState] = useSaveCampaignChanges();
    const [approveCampaign, approveCampaignState] = useApproveCampaign();
    const [rejectCampaign, rejectCampaignState] = useRejectCampaign();
    const [resubmitCampaign, resubmitCampaignState] = useResubmitCampaign();
    const [buttonLoadingState, setButtonLoadingState] = useState<ButtonLoadingState>("idle")

    useEffect(() => {
        setCampaignState(campaign);

    }, [campaign])

    const handleEdit = (key : keyof CampaignObject) => {
        
        return (value : string | number) => {

            let newVal;

            if (typeof value === "object" && value != null) {

                if (key === "parent_campaign") {
                    let selectedParent = parentCampaigns?.filter(p => p["parent_campaign_name"] === value["value"])
                    newVal = {
                        [key] : value["value"], 
                        "parent_campaign_id" : selectedParent[0]["parent_campaign_id"],
                        "marketing_program_id" : selectedParent[0]["marketing_program_id"]
                    };
                } else {
                    newVal =  {[key] : value["value"]};
                }
            } else {
                newVal = {[key] : value};
            }
            setCampaignState({...campaignState, ...newVal});
            if (key !== "rejection_reason") { setChanged(true); }
            
        }
    }
    
    return (

        <>
            <StyledLabel onClick={() => setOpen(!open)}>
                {campaignState.campaign_name_final === null ? campaignState.campaign_name : campaignState.campaign_name_final}
            </StyledLabel>
            <Drawer
                header={
                    <Flex>
                        {campaignState.campaign_name_final === null ? campaignState.campaign_name : campaign.campaign_name_final}
                    </Flex>
                }
                placement="right"
                size="lg"
                toggle={() => {
                    setOpen(!open);
                    setRefresh(true);
                    setButtonLoadingState("idle");
                }}
                isOpen={open}
                autoFocus={false}                
                outsideClickIgnoreClass="bcl-Portal bcl-portal"

            >
                <DrawerContainer className="drawer-container">
                    <TagContainer>  
                        <StyledTag className="fiscal">{"FY" + campaignState.fiscal_year}</StyledTag>
                        <StyledTag className="fiscal">{"Q" + campaignState.fiscal_quarter}</StyledTag>
                        <StyledTag className="region">{campaignState.campaign_region}</StyledTag>
                        <StyledTag className="region">{campaignState.campaign_subregion}</StyledTag>
                    </TagContainer>

                    <DetailsContainer>
                        {campaignState.approval_status === "Rejected" && (
                            <GroupCard title="Rejection Reason">
                                <div style={{color: "red"}}>
                                    {campaignState.rejection_reason}
                                </div>

                            </GroupCard>
                        )}
                        <GroupCard title="Campaign Details">
                            <StaticDetailsText title="Campaign Start Date:" field={campaign.start_date}/>
                            <EditableTextDetails title="Campaign Description:" 
                                field={campaignState.campaign_description} 
                                campaign={campaignState} 
                                handleChange={handleEdit("campaign_description")}/>
                            <StaticDetailsText title="Campaign Owner:" field={campaign.campaign_owner}/>
                        </GroupCard>

                        <GroupCard title="Campaign Fields">
                            <EditableSelectDetails title="Campaign Type:" 
                                field={campaignState.campaign_type} 
                                campaign={campaignState} 
                                handleChange={handleEdit("campaign_type")} 
                                options={formatOption(programs.map((p : MarketoProgramObject) => p["sfdc_type"]), false)}/>
                            <EditableSelectDetails title="Campaign Objective:" 
                                field={campaignState.campaign_objective} 
                                campaign={campaignState} 
                                handleChange={handleEdit("campaign_objective")} 
                                options={formatOption(CAMPAIGN_OBJECTIVE)}/>
                            <EditableSelectDetails title="Campaign Source:" 
                                field={campaignState.campaign_source} 
                                campaign={campaignState} 
                                handleChange={handleEdit("campaign_source")} 
                                options={formatOption(CAMPAIGN_SOURCE)}/>
                            <EditableSelectDetails title="Parent Campaign:" 
                                field={campaignState.parent_campaign} 
                                campaign={campaignState} 
                                handleChange={handleEdit("parent_campaign")} 
                                options={formatOption(parentCampaigns.map((p : ParentCampaignObject) => p["parent_campaign_name"]))}/>
                        </GroupCard>

                        <GroupCard title="Budget Details & Projections">
                            <EditableSelectDetails title="Budget Source:" 
                                field={campaignState.budget_source} 
                                campaign={campaignState} 
                                handleChange={handleEdit("budget_source")} 
                                options={formatOption(BUDGET_SOURCE)}/>
                            <EditableTextDetails title="Budget Amount ($):" 
                                field={campaignState.budget_amount} 
                                campaign={campaignState}
                                type="number" 
                                handleChange={handleEdit("budget_amount")}/>
                            <MetricsContainer>
                                <BadgeContainer className="projections">
                                    <EditableNumberBadge 
                                        title="Projected NNLs" 
                                        field={campaignState.nnl_projected_count} 
                                        campaign={campaignState} 
                                        color={colorGetter("blue", 500)} 
                                        handleChange={handleEdit("nnl_projected_count")}/>
                                    <EditableNumberBadge 
                                        title="Projected MQLs" 
                                        field={campaignState.mql_projected_count} 
                                        campaign={campaignState} 
                                        color={colorGetter("lightBlue", 500)} 
                                        handleChange={handleEdit("mql_projected_count")}/>
                                    <EditableNumberBadge 
                                        title="Projected SQLs" 
                                        field={campaignState.sql_projected_count} 
                                        campaign={campaignState} 
                                        color={colorGetter("lightGreen", 500)} 
                                        handleChange={handleEdit("sql_projected_count")}/>
                                    <EditableNumberBadge 
                                        title="Projected Pipe Count" 
                                        field={campaignState.pipe_count_projected} 
                                        campaign={campaignState} 
                                        color={colorGetter("orange", 500)} 
                                        handleChange={handleEdit("pipe_count_projected")}/>
                                    <EditableNumberBadge 
                                        title="Projected Pipe Amount" 
                                        field={campaignState.pipe_amount_projected} 
                                        campaign={campaignState} 
                                        color={colorGetter("red", 500)} 
                                        handleChange={handleEdit("pipe_amount_projected")}/>
                                </BadgeContainer>
                                <BadgeContainer className="costs">
                                    <LabeledBadge 
                                        label="Cost Per NNL" 
                                        value={campaignState.nnl_projected_count === 0 ? 0 : Math.round((campaignState.budget_amount/campaignState.nnl_projected_count + Number.EPSILON)*100)/100} 
                                        color={colorGetter("blue", 500)}/>
                                    <LabeledBadge 
                                        label="Cost Per MQL" 
                                        value={campaignState.mql_projected_count === 0 ? 0 : Math.round((campaignState.budget_amount/campaignState.mql_projected_count + Number.EPSILON)*100)/100}
                                        color={colorGetter("lightBlue", 500)}/>
                                    <LabeledBadge 
                                        label="Cost Per SQL" 
                                        value={campaignState.sql_projected_count === 0 ? 0 : Math.round((campaignState.budget_amount/campaignState.sql_projected_count + Number.EPSILON)*100)/100}
                                        color={colorGetter("lightGreen", 500)}/>
                                    <LabeledBadge 
                                        label="Cost Per Pipe" 
                                        value={campaignState.pipe_count_projected === 0 ? 0 : Math.round((campaignState.budget_amount/campaignState.pipe_count_projected + Number.EPSILON)*100)/100}
                                        color={colorGetter("orange", 500)}/>
                                    <LabeledBadge 
                                        label="Pipeline to Spend Ratio" 
                                        value={Math.round((campaignState.pipe_amount_projected/campaignState.budget_amount + Number.EPSILON)*100)/100}
                                        color={colorGetter("red", 500)}/>
                                </BadgeContainer>
                            </MetricsContainer>
                        </GroupCard>
                        <Button disabled={!changed} onClick={() => {
                            saveChanges(campaignState);
                            setChanged(false);
                        }}>Save Changes</Button>
                    </DetailsContainer>
                    {campaignState.approval_status === "Rejected" && (

                        <ApproveRejectButtonFlex>
                            <Button  
                                intent="warning" 
                                loadingState={buttonLoadingState}
                                onClick={()=>{
                                        setButtonLoadingState("loading");
                                        resubmitCampaign(campaignState)
                                            .then(r => setButtonLoadingState("success"))
                                            .finally(() => setStatus("Submitted"));
                                    }
                                }>
                                    Resubmit
                                    <Icon name={"circle-xmark"}/>
                            </Button>
                        </ApproveRejectButtonFlex>
                    )}
                    {campaignState.approval_status !== "Rejected" && (
                        <ApproveRejectButtonFlex>
                            <ApproveButton 
                                className="approve-button" 
                                intent="success" 
                                onClick={()=>{
                                    setButtonLoadingState("loading")
                                    approveCampaign(campaignState)
                                        .then(r => setButtonLoadingState("success"))
                                        .finally(() => setStatus("Approved - Pending MarOps Review"));
                                    
                                }} 
                                loadingState={buttonLoadingState} 
                                disabled={campaignState.approval_status==="Submitted" ? false : true}
                            >
                                    Approve
                                    <Icon name={"circle-check"}/>
                            </ApproveButton>
                            
                            <Button  
                                intent="danger" 
                                loadingState={buttonLoadingState}
                                onClick={()=> {
                                    setButtonLoadingState("loading");
                                    rejectCampaign(campaignState)
                                        .then(r => setButtonLoadingState("success"))
                                        .finally(() => setStatus("Rejected"))
                                }}
                                disabled={campaignState.approval_status==="Submitted" && campaignState.rejection_reason !== "" && campaignState.rejection_reason !== null ? false : true}>
                                    Reject
                                    <Icon name={"circle-xmark"}/>
                            </Button>

                            <Label>
                                Rejection Reason (Required):
                                <Textarea vSize="lg" fullWidth
                                    onChange={(e) => {
                                        const handleChange = handleEdit("rejection_reason");
                                        handleChange(e.target.value);
                                    }}
                                    placeholder={campaignState.rejection_reason || ""}
                                />
                            </Label>
                        </ApproveRejectButtonFlex>
                    )}                
                </DrawerContainer>
                <DrawerFooter style={{boxShadow:"0px 0px 0px"}}>
                    <Flex style={{width: "100%", display: "flex", justifyContent:"center"}}>
                        <StatusTag status={status}/>
                    </Flex>
                </DrawerFooter>
            </Drawer>
            
        </>
    )
}

const ApproveRejectButtonFlex = styled(Flex)`

    margin-top: 2rem;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;
`

interface EditableDetailsProps {
    title: string;
    field: string|number;
    campaign: CampaignObject;
    options?: OptionsList[];
    type?: "number"|"text";
    handleChange: Function;
}
const EditableTextDetails = (props : EditableDetailsProps) => {
    
    const [inv, setInv] = useState(false);
    const [fieldState, setFieldState] = useState(props.field)

    // const inputStyle = {
    //     display: "flex",
    //     width: "auto",
    //     fontSize: "14px",
    //     marginLeft: "1rem",
    //     fontWeight: "1",
    //     marginBottom: "0px",
    //     height: "100%",
    // }

    if (EDITABLE_STATUS.includes(props.campaign.approval_status)) {
        return (
            <EditableContainer>
                <Label className="title">{props.title}</Label>
                <StyledInput 
                    // style={inputStyle}
                    className="text-field" 
                    placeholder=""
                    value={fieldState}
                    vSize="md"
                    type={props.type}
                    step={1000}
                    min={1}
                    invalid={inv}
                    onChange={(e: ChangeEvent<HTMLInputElement>, action : OnChangeAction) => {
                        if (props.type){
                            if (action === "cancel") {setFieldState(props.field)}
                            else if (action === "clear") {
                                setFieldState(e.target.valueAsNumber);
                                setInv(true);}
                            else if (e.target.valueAsNumber <= 0 || e.target.value === "") {setInv(true);}
                            else if (action === "save" && inv === false) {
                                props.handleChange(e.target.valueAsNumber)
                                setFieldState(e.target.valueAsNumber)}
                            else {setInv(false);}
                        } else {
                            
                            if (action === "cancel") {setFieldState(props.field)}
                            else if (action === "clear") {
                                setFieldState(e.target.value);
                                setInv(true);}
                            else if (e.target.value === "") {setInv(true);}
                            else if (action === "save" && inv === false) {
                                setFieldState(e.target.value)
                                props.handleChange(e.target.value)}
                            else {setInv(false);}
                        }
                    }}
                />
            </EditableContainer>
    )}
    else {
        
        return (<StaticDetailsText title={props.title} field={props.field}/>)
}};

const EditableSelectDetails = (props: EditableDetailsProps) => {
    if (props.field === "") {return null;}
    else if (props.campaign.approval_status === "Submitted" || props.campaign.approval_status === "Rejected") {
        return (
            <EditableContainer style={{justifyContent: "space-between"}}>
                <Label className="title">{props.title}</Label>
                <Select 
                    className="field"
                    value={{"label" : props.field, "value" : props.field}}
                    onChange={(selectedOption) => {
                        props.handleChange(selectedOption)
                    }}
                    isClearable={false}
                    options={props.options}
                />
            </EditableContainer>
    )}
    else {
        return (<StaticDetailsText title={props.title} field={props.field}/>)
}};

const StaticDetailsText = ({title, field} : {title : string, field : string|number}) => {
    
    
    return (field === "") ? null : (
        <LabelContainer>
            <Label className="title">{title}</Label>
            <Label className="field">{field}</Label>
        </LabelContainer>
        
    )
}

const EditableContainer = styled(Flex)`
    display: flex;
    align-items: center;
    .title {
        font-size: 14px;
        margin-bottom: 0px;
        // width: 40%;
        
    }
    .field {
        margin-left: 1rem;
        width: 70%;
    }
    
    .bcl-inline-input-view-mode {
        display: flex;
        width: auto;
        font-size: 14px;
        margin-left: 1rem;
        font-weight: normal;
        margin-bottom: 0px;
        height: 100%;
    }
`
const StyledInput = styled(InlineInput)`
    display: flex;
    width: auto;
    font-size: 14px;
    margin-left: 1rem;
    font-weight: normal;
    margin-bottom: 0px;
    height: 100%;
`


interface EditableBadgeProps extends Omit<EditableDetailsProps, 'field'> {
    color: string|undefined;
    field: number;
} 

const EditableNumberBadge = (props : EditableBadgeProps) => {
    
    const [inv, setInv] = useState(false);
    const [fieldState, setFieldState] = useState(props.field);

    if (EDITABLE_STATUS.includes(props.campaign.approval_status)) {
        return (

            <FlexBox style={{justifyContent:"space-between", alignItems:"center", height: "100%"}}>
                <Label style={{marginRight: "2rem", fontSize:"14px", marginBottom:"0rem"}}>{props.title + ": "}</Label>
                <StyledInput 
                    fullWidth
                    placeholder=""
                    value={fieldState}
                    type="number"
                    step={1000}
                    style={{marginRight:"0rem"}}
                    invalid={inv}
                    onChange={(e: ChangeEvent<HTMLInputElement>, action : OnChangeAction) => {
                        if (action === "clear") {
                            setFieldState(0);
                            props.handleChange(0) }
                        else if (e.target.valueAsNumber < 0 || e.target.value === "") {
                            setInv(true);}
                        else if (action === "save" && inv === false) {
                            setFieldState(e.target.valueAsNumber)
                            props.handleChange(e.target.value)}
                        else {setInv(false);}
                    }}
                >
                    <Badge value={props.field} style={{color: props.color}} variant="muted" size="lg" truncateAfter={false}/>
                </StyledInput>
            </FlexBox>
        
    )}
    else {
        
        return (<LabeledBadge label={props.title} value={props.field} color={props.color}/>)
}};

const LabeledBadge = ({label, value, color}:{label:string, value:number, color:string|undefined}) => {

    return (
        <FlexBox style={{justifyContent:"space-between", alignItems:"center", height: "100%"}}>
            <Label style={{marginRight: "0.5rem", fontSize:"14px", marginBottom:"0rem"}}>{label + ": "}</Label>
            <Badge value={value} style={{color: color}} variant="muted" size="lg" truncateAfter={false}/>
        </FlexBox>
    )
}




const MetricsContainer = styled(Flex)`
    flex-direction: row;
    justify-content: space-between;

    .projections {
        min-width: 40%;
        width: auto;
        margin-right: 1rem;
    }
    .costs {
        min-width: 30%;
    }
`

const BadgeContainer = styled(Flex)`

    // justify-content: space-around;
    direction: column;
    // min-height: 120%;
    flex-direction: column;
    // width: 100%;
    // height: 100%;
    // align-items: center;
    
    > * {
        &:not(:last-of-type) {
        margin-bottom: 0.5rem;
        }
    }

`



const GroupCard = ({title, children} : {title? : string, children : React.ReactNode}) => {

    return title != null ? (
        <GroupContainer>
            <Heading level={3} style={{marginBottom: "1rem"}}>{title}</Heading>
            <InsideContainer>{children}</InsideContainer>
        </GroupContainer>
    ) : (
    <GroupContainer>
        <InsideContainer>{children}</InsideContainer>
    </GroupContainer>
    )
}

const GroupContainer = styled(Flex)`
    flex-direction: column;
    
`


const InsideContainer = styled(Flex)`
    background: #fff;
    border-radius: 1.5em;
    box-shadow: 0px 12px 24px rgba(69, 84, 94, 0.1);
    border: 1px solid rgba(0, 0, 0, 0.05);
    padding: 2rem 2rem;
    flex-direction: column;
    margin-bottom: 2rem;
    > * {
        &:not(:last-of-type) {
        margin-bottom: 1.5rem;
        }
    }
    
`

const LabelContainer = styled(Flex)`
    .title {
        font-size: 14px;
        margin-bottom: 0px;
    }
    .field {
        font-size: 14px;
        margin-left: 1rem;
        font-weight: normal;
        margin-bottom: 0px;
    }
    // > * {
    //     &:not(:last-of-type) {
    //     margin-bottom: 0.5rem;
    //     }
    // }
`

const ApproveButton = styled(Button)`
    width: 100%
    background: blue;
`

const ButtonBox = styled(FlexBox)`
    display: flex;
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 2rem;
    justify-content: space-between;
    align-items: center;
    .approve-button {
        display: flex !important;
        flex-grow: 1 !important;
        width: 100%;
        // background: yellow;
    }
`

const DrawerContainer = styled(DrawerBody)`
    display: flex;
    flex-direction: column;
    .drawer-container {
        // width: 100%;
        // justify-content: center;
        background: red;

    }

    // padding-left: 0rem !important;
    margin-top: 1rem !important;
    
    
`

const DetailsContainer = styled(FlexBox)`
    display: flex;
    flex-direction: column;
    margin-top: 2rem;

    .campaign-type {
        margin-top: 1rem;
    }
`

const StyledTag = styled(Tag)`
    justify-content: center;
    width: 100%;
    font-size: 14px;
    .status {
        // width: 4rem;
        background: yellow;
    }
`

const TagContainer = styled(FlexBox)`
    flex-direction: row;
    display: flex;
    justify-content: space-between;
    width: 100%;
    
    .fiscal {
        background: ${getColor("lightBlue", 500)};
    }
    .region {
        background: ${getColor("red", 500)}
    }

    > * {
        &:not(:last-of-type) {
        margin-right: 1rem;
        }
    }
    
`

const StyledLabel = styled(Label)`
    cursor: pointer;
    font-size: 16px;
    border-bottom: 0px !important;
`